import { useCallback, useEffect, useMemo, useState } from 'react'
import { getFeathersClient } from '../pages/chat/feathers-client'

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported'

export type PushSubscriptionJSON = ReturnType<PushSubscription['toJSON']> | null

function b64UrlToUint8Array(base64Url: string) {
	const pad = '='.repeat((4 - (base64Url.length % 4)) % 4)
	const base64 = (base64Url + pad).replace(/-/g, '+').replace(/_/g, '/')
	const raw = atob(base64)
	const output = new Uint8Array(raw.length)
	for (let i = 0; i < raw.length; i++)
		output[i] = raw.charCodeAt(i)
	return output
}

function getUserDeviceIdKey(userId: string | null | undefined): string | null {
	if (!userId)
		return null
	return `deviceId_${userId}`
}

function getUserDeviceId(userId: string | null | undefined): string | null {
	const key = getUserDeviceIdKey(userId)
	if (!key)
		return null
	return localStorage.getItem(key)
}

function setUserDeviceId(userId: string | null | undefined, deviceId: string): void {
	const key = getUserDeviceIdKey(userId)
	if (!key)
		return
	localStorage.setItem(key, deviceId)
}

function clearUserDeviceId(userId: string | null | undefined): void {
	const key = getUserDeviceIdKey(userId)
	if (!key)
		return
	localStorage.removeItem(key)
}

function isPwaStandalone(): boolean {
	const mq = '(display-mode: standalone)'
	return (typeof window !== 'undefined') &&
		((window.matchMedia && window.matchMedia(mq).matches) ||
			//// @ts-expect-error safari iOS
			(typeof navigator !== 'undefined' && !!(navigator as any).standalone))
}

export interface UseNotificationsOptions {
	serviceWorkerPath?: string
	defaultIconUrl?: string
	userId?: string | null
}

export interface ForegroundNotifyOpts {
	title: string
	body?: string
	icon?: string
	tag?: string
	url?: string
	badge?: string
}

interface BackendSubscription {
	device_id: string
	endpoint: string
	created_at: number
	is_active: boolean
}

export function useNotifications(opts: UseNotificationsOptions = {}) {
	const serviceWorkerPath = opts.serviceWorkerPath ?? '/sw.js'
	const defaultIcon = opts.defaultIconUrl
	const userId = opts.userId

	const [permission, setPermission] = useState<PermissionState>(() => {
		if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
		return Notification.permission;
	})

	const [swReady, setSwReady] = useState<ServiceWorkerRegistration | null>(null)
	const [subscription, setSubscription] = useState<PushSubscription | null>(null);
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<unknown>(null)
	const [backendSubscriptions, setBackendSubscriptions] = useState<BackendSubscription[]>([])
	const [syncing, setSyncing] = useState(false)
	const [hasCompletedInitialSync, setHasCompletedInitialSync] = useState(false)

	const supportsNotifications = useMemo(
		() => typeof window !== 'undefined' && 'Notification' in window,
		[]
	)

	const supportsServiceWorker = useMemo(
		() => typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
		[]
	)

	const supportsPushManager = useMemo(
		() => !!swReady?.pushManager,
		[swReady]
	)

	useEffect(() => {
		let mounted = true
		;(async () => {
			if (!supportsServiceWorker)
				return
			try {
				const reg = await navigator.serviceWorker.register(serviceWorkerPath)
				const ready = await navigator.serviceWorker.ready
			if (!mounted)
				return
			setSwReady(ready || reg)
		} catch (e) {
			if (!mounted)
				return
			setError(e)
		}
		})()
		return () => { mounted = false }
	}, [serviceWorkerPath, supportsServiceWorker])

	useEffect(() => {
		if (!supportsNotifications || typeof navigator?.permissions === 'undefined')
			return
		navigator.permissions.query({ name: 'notifications' as PermissionName })
		.then((status) => {
			status.onchange = () => {
				setPermission(Notification.permission)
			}
		})
		.catch(() => {
			console.warn('Failed to query notifications permission')
		})
	}, [supportsNotifications])

	const askPermission = useCallback(async (): Promise<PermissionState> => {
		if (!supportsNotifications)
			return 'unsupported'
		const res = await Notification.requestPermission()
		setPermission(res)
		return res
	}, [supportsNotifications])

	const syncWithBackend = useCallback(async () => {
		if (!userId)
			return

		setSyncing(true)
		try {
			const feathersClient = getFeathersClient()
			const subs = await feathersClient.service('pushSubscriptions').find() as BackendSubscription[]
			setBackendSubscriptions(subs)

			const currentDeviceId = getUserDeviceId(userId)
			if (!currentDeviceId) {
				return
			}

			const backendHasDevice = subs.some(sub => sub.device_id === currentDeviceId)

			if (swReady?.pushManager) {
				const browserSub = await swReady.pushManager.getSubscription()
				if (browserSub && backendHasDevice) {
					setSubscription(browserSub)
				} else if (browserSub && !backendHasDevice) {
					console.warn('Browser has subscription but backend does not. Clearing local subscription state.')
					setSubscription(null)
				} else if (!browserSub && backendHasDevice) {
					console.warn('Backend has subscription but browser does not. This may be from a different device.')
				}
			}
		} catch (error) {
			console.warn('Failed to sync with backend:', error)
		} finally {
			setSyncing(false)
			setHasCompletedInitialSync(true)
		}
	}, [userId, swReady])

	useEffect(() => {
		if (!userId) {
			setBackendSubscriptions([])
			setSubscription(null)
			setHasCompletedInitialSync(false)
			return
		}

		syncWithBackend()
	}, [userId, syncWithBackend])

	const registerSubscriptionWithBackend = useCallback(async (subscription: PushSubscription) => {
		try {
			if (!userId)
				throw new Error('User not authenticated')

			const deviceId = getUserDeviceId(userId) || crypto.randomUUID()
			setUserDeviceId(userId, deviceId)

			const feathersClient = getFeathersClient()
			const keys = subscription.toJSON().keys
			if (!keys)
				throw new Error('Subscription keys not available')

			await feathersClient.service('pushSubscriptions').create({
				endpoint: subscription.endpoint,
				keys: {
					p256dh: keys.p256dh,
					auth: keys.auth
				},
				device_id: deviceId
			})
		} catch (error) {
			console.error('Failed to register subscription with backend:', error)
			throw error
		}
	}, [userId])

	const ensureSubscription = useCallback(async (vapidPublicKeyB64Url: string) => {
		setLoading(true)
		setError(null)
		try {
			if (!supportsServiceWorker)
				throw new Error('ServiceWorker unsupported');
			const reg = swReady ?? (await navigator.serviceWorker.ready);
			if (!reg?.pushManager)
				throw new Error('PushManager unsupported (non-PWA iOS Safari ?)')
			if (!supportsNotifications || Notification.permission !== 'granted') {
				const p = await askPermission()
				if (p !== 'granted')
					throw new Error(`Permission was ${p}`)
			}
			const existing = await reg.pushManager.getSubscription()
			if (existing) {
				// Try to register existing subscription with backend (in case it wasn't registered before)
				try {
					await registerSubscriptionWithBackend(existing)
				} catch (e) {
					// Ignore errors when registering existing subscription
					console.warn('Failed to register existing subscription:', e)
				}
				setSubscription(existing)
				setLoading(false)
				return existing
			}
			const appServerKey = b64UrlToUint8Array(vapidPublicKeyB64Url)
			const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey })

			// Register new subscription with backend
			await registerSubscriptionWithBackend(sub)

			setSubscription(sub)
			setLoading(false)
			return sub
		} catch (e) {
			setError(e)
			setLoading(false)
			throw e
		}
	}, [askPermission, registerSubscriptionWithBackend, swReady, supportsNotifications, supportsServiceWorker])

	const unsubscribe = useCallback(async () => {
		if (!subscription)
			return false
		try {
			const deviceId = getUserDeviceId(userId)
			if (deviceId) {
				try {
					const feathersClient = getFeathersClient()
					await feathersClient.service('pushSubscriptions').remove(deviceId)
				} catch (error) {
					console.warn('Failed to remove subscription from backend:', error)
				}
			}

			const ok = await subscription.unsubscribe()
			setSubscription(null)

			clearUserDeviceId(userId)

			return ok
		} catch (e) {
			setError(e)
			return false
		}
	}, [subscription, userId])

	const notifyForeground = useCallback(async (p: ForegroundNotifyOpts) => {
		try {
			const reg = swReady ?? (supportsServiceWorker ? await navigator.serviceWorker.ready : null)
			const title = p.title
			const body = p.body
			const icon = p.icon ?? defaultIcon
			const data = { url: p.url }

			if (reg && 'showNotification' in reg) {
				return reg.showNotification(title, { body, icon, tag: p.tag, data, badge: p.badge })
			}
			if (supportsNotifications && Notification.permission === 'granted') {
				new Notification(title, { body, icon, tag: p.tag, data })
				return
			}
		} catch (e) {
			setError(e);
		}
	}, [defaultIcon, supportsNotifications, supportsServiceWorker, swReady]);

	const shouldUseSystemNotificationForChat = useCallback((chatId: string) => {
		const visible = document.visibilityState === 'visible' && document.hasFocus?.()
		const onSameChat = typeof window !== 'undefined' && window.location.hash.includes(`/chat/${chatId}`)
		return !(visible && onSameChat)
	}, [])

	const subscriptionJSON: PushSubscriptionJSON = useMemo(
		() => subscription?.toJSON?.() ?? null,
		[subscription]
	)

	const isEnabledInBackend = useMemo(() => {
		if (!userId)
			return false
		const deviceId = getUserDeviceId(userId)
		if (!deviceId)
			return false
		return backendSubscriptions.some(sub => sub.device_id === deviceId)
	}, [userId, backendSubscriptions])

	const isFullyEnabled = useMemo(() => {
		return !!subscription && isEnabledInBackend
	}, [subscription, isEnabledInBackend])

	const isInitializing = useMemo(() => {
		return !hasCompletedInitialSync
	}, [hasCompletedInitialSync])

	return {
		loading,
		error,
		permission,
		subscription,
		subscriptionJSON,
		syncing,
		isInitializing,
		backendSubscriptions,
		isEnabledInBackend,
		isFullyEnabled,

		supportsNotifications,
		supportsServiceWorker,
		supportsPushManager,
		isPwaStandalone: isPwaStandalone(),

		askPermission,
		ensureSubscription,
		unsubscribe,
		syncWithBackend,
		notifyForeground,
		shouldUseSystemNotificationForChat,
	}
}
